import React, { Fragment } from 'react';
import { gql, useQuery } from 'urql';
import { HacpackQuery, Handling, HacpackQueryHandlingArguments } from '../types/graph';
import { GraphDeepPartialNotListed, GraphDeepPartialOverride } from '../types';
import { every, map, isNull, filter } from 'lodash';
import { CenteredContainer, Loading } from '.';
import { Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import styled from '../themes/themed-styled-components';

type PlacardsAndMarkingsQueryData = GraphDeepPartialOverride<HacpackQuery, {
    handling: PlacardsAndMarkingsHandling
}>

type PlacardsAndMarkingsHandling = GraphDeepPartialNotListed<Handling, 'id' | 'placardsAndMarkings'>

type PlacardsAndMarkingsQueryArguments = HacpackQueryHandlingArguments;

const placardsAndMarkingsQuery = gql<PlacardsAndMarkingsQueryData, PlacardsAndMarkingsQueryArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        id
        placardsAndMarkings
    }
}`;

type TerminalHandlingPlacardsAndMarkingsProps = {
    handlingId: number
}

const PlacardsAndMarkingsPositions = styled.div`
min-height: 100%;
display: flex;
flex-direction: column;
padding: 15px;
gap: 15px;
justify-content: space-between;

h5 {
    position: sticky;
    top: 0;
    background-color: ${({ theme: { white } }) => white};
    margin: 0;
    padding: 6px 0;
}

`;

const PlacardsAndMarkingsPosition = styled.div`
display: flex;
flex-direction: column;
flex-wrap: wrap;
justify-content: space-evenly;

gap: 15px;
`;

const PlacardsAndMarkingsAlternatives = styled.div`

background-color: ${({ theme: { gray300 } }) => gray300};

display: flex;
flex-direction: column;
gap: 1em;
padding: 15px;

> div {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: stretch;
    gap: 15px;

    > div {
    }
}

`;

const PlacardsAndMarkingsGroups = styled.div`
display: flex;
flex-direction: row;
flex-wrap: wrap;
align-items: center;
gap: 1em;

> div {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background-color: ${({ theme: { white } }) => white};

    > div {
        flex: 1;
    }
}
`;

const PlacardsAndMarkingsPlacard = styled.div`
padding: 15px;
> svg {
    height: 120px;
    width: 120px;
}
`;

const PlacardsAndMarkingsMarking = styled.div`
padding: 15px;
p {
    font-family: ${({ theme: { fontFamilyMonospace } }) => fontFamilyMonospace};
    margin: 0;
}
`;

const PlacardsAndMarkingsDisclaimer = styled(Alert)`
margin-bottom: 0;
`;


const TerminalHandlingPlacardsAndMarkings = ({ handlingId: id }: TerminalHandlingPlacardsAndMarkingsProps) => {

    const [result] = useQuery<PlacardsAndMarkingsQueryData, PlacardsAndMarkingsQueryArguments>({
        query: placardsAndMarkingsQuery,
        variables: { id }
    });

    const { t } = useTranslation('common');

    const { data, fetching, error } = result;

    const placardsAndMarkings = data?.handling?.placardsAndMarkings;

    if (fetching === true && placardsAndMarkings === undefined) {
        return <Loading />;
    }



    if (error !== undefined) {
        return <CenteredContainer>
            <Alert color="danger">{t('Si è verificato un errore durante il recupero dei dati.')}</Alert>
        </CenteredContainer>;
    }

    if (every(placardsAndMarkings, isNull)) {
        return <CenteredContainer>
            <Alert color="success">{t('Nessuna etichetta necessaria.')}</Alert>
        </CenteredContainer>;
    }

    return <PlacardsAndMarkingsPositions>
        {map(placardsAndMarkings, (position, key) => {
            if (position === null) {
                return <Fragment key={key} />;
            }

            return <div key={key}>
                <h5>{t(`placardsAndMarkings.positions.${key}`)}</h5>
                <PlacardsAndMarkingsPosition>{position.map((alternatives, i) => {
                    const nonEmptyAlternatives = filter(alternatives, ({ length }) => length > 0);
                    const count = nonEmptyAlternatives.length;
                    const isOptional = count < alternatives.length;
                    const hasAlternatives = count > 1;

                    const title = i === 0
                        ? hasAlternatives === true
                            ? isOptional === true
                                ? <h6>{t('placardsAndMarkings.alternatives.alternativesOptional', { count })}</h6>
                                : <h6>{t('placardsAndMarkings.alternatives.alternatives', { count })}</h6>
                            : isOptional === true
                                ? <h6>{t('placardsAndMarkings.alternatives.optional')}</h6>
                                : null
                        : hasAlternatives === true
                            ? isOptional === true
                                ? <h6>{t('placardsAndMarkings.alternatives.andAlternativesOptional', { count })}</h6>
                                : <h6>{t('placardsAndMarkings.alternatives.andAlternatives', { count })}</h6>
                            : isOptional === true
                                ? <h6>{t('placardsAndMarkings.alternatives.andOptional')}</h6>
                                : <h6>{t('placardsAndMarkings.alternatives.and')}</h6>

                    return <PlacardsAndMarkingsAlternatives key={i}>
                        {title}
                        <div>{
                            nonEmptyAlternatives.map((group, j) => {
                                if (group.length === 0) {
                                    return <Fragment key={j} />;
                                }


                                const orElse = j > 0 ? <span>{t('placardsAndMarkings.alternatives.or')}</span> : null;

                                return <PlacardsAndMarkingsGroups key={j}>{orElse}<div>{
                                    group.map(({ name, image, text }, k) => {
                                        if (image === null) {
                                            return <PlacardsAndMarkingsMarking key={k}>{
                                                map(text, (t, m) => <p key={m}>{t}</p>)
                                            }</PlacardsAndMarkingsMarking>
                                        }

                                        return <>
                                            <PlacardsAndMarkingsPlacard key={k} dangerouslySetInnerHTML={{ __html: image }} />
                                        </>;
                                    })
                                }</div></PlacardsAndMarkingsGroups>
                            })
                        }</div>
                    </PlacardsAndMarkingsAlternatives>
                })}</PlacardsAndMarkingsPosition>
            </div>
        })}
        <PlacardsAndMarkingsDisclaimer color={'warning'}>
            {t('placardsAndMarkings.disclaimer')}
        </PlacardsAndMarkingsDisclaimer>
    </PlacardsAndMarkingsPositions>

};

export default TerminalHandlingPlacardsAndMarkings;